// OPFS-Store für PMTiles — Wave 14bd
// ===================================
// Origin Private File System (OPFS) statt CacheStorage als Storage-Layer für
// die grossen PMTiles-Dateien.
//
// Warum der Wechsel?
// ------------------
// CacheStorage hat auf iOS Safari zwei strukturelle Nachteile, die bei 100+ MB
// Files hart ins Gewicht fallen:
//   1) `cache.match(url).then(r => r.blob())` liest die komplette Datei einmal
//      in den Memory — auf Kaltstart bei freiem Speicher ist das ein ~10-15s
//      Disk-Read pro Datei (Map + Contours + Hillshade ≈ 30-45s initial).
//      MapLibre wartet in der Zwischenzeit; der User sieht eine halbfertige
//      Karte. Exakt das Problem das der Kumpel kritisiert hat.
//   2) Response-/Vary-/Range-Header-Semantik. Selbst mit ignoreVary auf unserer
//      Seite hat Workbox parallel geschrieben und auf Safari haben wir immer
//      wieder Vary:Range zurückbekommen — Offline-Lookups sind stille Fehler.
//
// OPFS adressiert beides:
//   1) `fileHandle.getFile()` gibt eine `File`-Referenz zurück ohne den Inhalt
//      in den Memory zu ziehen. `file.slice(offset, length).arrayBuffer()`
//      liest nur die angeforderten Bytes — disk-backed, O(KB) statt O(MB) pro
//      Tile-Request.
//   2) Kein HTTP-Semantik-Overhead. OPFS ist ein "stummer" Filestore, keine
//      Vary-Header, keine Range-Requests, keine Workbox-Races.
//
// API-Parität mit lib/offline.ts
// ------------------------------
// Diese Module ersetzt die PMTiles-Funktionen aus lib/offline.ts. Die API-Form
// ist analog (downloadToOpfs, opfsStatus, removeFromOpfs, clearAllOpfs), damit
// der Callsite-Umbau minimal ist. lib/offline.ts bleibt für nicht-PMTiles-
// Artefakte (wenn wir welche bekommen) und für formatBytes/getStorageEstimate.
//
// Atomic-Write-Strategie
// ----------------------
// OPFS hat keinen portablen `rename()` (Safari iOS supports `move()` nicht in
// allen Versionen). Unser Ansatz:
//   - Write direkt auf Zieldateinamen. Während des Schreibens ist die Datei
//     "unfertig" — die Länge wächst.
//   - Bei Abort/Fehler: writable.abort() + removeEntry() — Zieldatei ist weg.
//   - Bei Erfolg: writable.close() — Datei ist fertig.
//   - Konsumenten (FirstLaunchGate) vergleichen Filesize mit manifest.sizeBytes
//     und verwerfen + re-downloaden, wenn die Grösse nicht passt. Das fängt
//     den Fall "App wurde während Download geschlossen" zuverlässig.
//
// Browser-Support
// ---------------
// OPFS ist seit iOS 15.2 / Safari 15.2 stabil in allen Kontexten (main thread
// reads + writes, inkl. streaming). FileSystemWritableFileStream ist seit
// Safari 16 offiziell dabei. Auf älteren Browsern wirft `getDirectory()`
// einen Error — wir geben das an den Aufrufer weiter, der es mit einem
// sinnvollen Fehlerzustand handhaben soll.

/** Unter-Verzeichnis im OPFS-Root; hält alle PMTiles-Dateien isoliert. */
const PMTILES_DIR = 'pmtiles';

/**
 * Wave 14ce-fix: Window-Event, das gefeuert wird sobald eine PMTiles-Datei
 * im OPFS geändert wurde (Download abgeschlossen, gelöscht, oder Clear All).
 *
 * Hintergrund: lib/map/pmtiles.ts cached die `File`-Referenz pro
 * `OpfsPmtilesSource` einmal beim ersten getBytes(). `File` ist ein Snapshot
 * des damaligen OPFS-Inhalts — wenn die Datei danach via downloadToOpfs
 * (Re-Download in der OfflinePage) ausgetauscht wird, sieht der Source
 * weiterhin die ALTEN Bytes. MapLibre rendert dann das alte PMTiles, auch
 * wenn auf Disk schon die neue Multi-Region-Version liegt → "Everest
 * verschwindet nachdem ich die Liguria-Map runtergeladen habe"-Bug.
 *
 * `detail.filename` ist der ABGELEITETE Dateiname (z.B.
 * "liguria_contours.pmtiles") — bewusst NICHT die volle URL, weil
 * OfflinePage mit relativer Manifest-URL ("/tiles/...") arbeitet, MapView
 * aber mit absoluter Origin-URL ("https://.../tiles/..."). Beide Seiten
 * können denselben Dateinamen via `opfsFilenameFromUrl(url)` ableiten,
 * also wird das Match darüber gelegt — sonst greift die Invalidierung
 * dank URL-Form-Mismatch nie.
 *
 * `null` = "alle Files invalidieren" (clearAllOpfs).
 */
export const OPFS_PMTILES_UPDATED_EVENT = 'adt:opfs-pmtiles-updated';

export interface OpfsPmtilesUpdatedDetail {
  /** Dateiname (z.B. "liguria.pmtiles"). `null` = alle Files. */
  filename: string | null;
}

function dispatchOpfsUpdated(url: string | null): void {
  if (typeof window === 'undefined') return;
  // url=null → filename=null (= "alle invalidieren"). Sonst extrahieren wir
  // den Dateinamen aus der URL (egal ob relativ oder absolut), damit der
  // Listener auch matcht wenn die Quelle die andere URL-Form benutzt.
  const filename = url === null ? null : opfsFilenameFromUrl(url);
  window.dispatchEvent(
    new CustomEvent<OpfsPmtilesUpdatedDetail>(OPFS_PMTILES_UPDATED_EVENT, {
      detail: { filename },
    }),
  );
}

/**
 * Dateiname aus der absoluten URL ableiten. PMTiles-URLs sehen so aus:
 *   https://staging.tomjanas.com/tiles/liguria.pmtiles
 * → "liguria.pmtiles"
 *
 * Wir trimmen Query-Strings und Hash-Fragments, damit `?_sw=bypass` nicht
 * im Filenamen landet.
 */
export function opfsFilenameFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop();
    if (!last) throw new Error('URL hat keinen Pfadnamen');
    return last;
  } catch {
    // Fallback: letztes Segment zwischen `/` und `?`/`#` raussuchen.
    const clean = url.split('?')[0]!.split('#')[0]!;
    const last = clean.split('/').filter(Boolean).pop();
    if (!last) throw new Error(`Kann Dateinamen nicht aus URL ableiten: ${url}`);
    return last;
  }
}

/**
 * Öffnet (oder legt an) das `pmtiles/`-Unterverzeichnis im OPFS-Root. Alle
 * anderen Funktionen gehen über diese Handle. Die OPFS-API ist leider nicht
 * singleton: jedes getDirectory() macht einen neuen Handle-Objekt, das aber
 * auf dasselbe Storage zeigt.
 */
async function getPmtilesDir(): Promise<FileSystemDirectoryHandle> {
  if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) {
    throw new Error('OPFS is not supported by this browser');
  }
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(PMTILES_DIR, { create: true });
}

export interface OpfsStatus {
  /** true wenn eine Datei mit dem URL-abgeleiteten Namen existiert. */
  exists: boolean;
  /** Bytes; nur gesetzt wenn exists=true. */
  size: number;
}

/**
 * Prüft, ob eine PMTiles-Datei für die gegebene URL im OPFS liegt. Gibt zurück:
 *   { exists: true, size: n }  — da und n Bytes gross
 *   { exists: false, size: 0 } — nicht da, oder OPFS nicht verfügbar
 *
 * Wirft NICHT bei fehlender OPFS-Unterstützung — der Aufrufer soll dann
 * wissen "nicht da" und darüber im UI differenzieren können.
 */
export async function opfsStatus(url: string): Promise<OpfsStatus> {
  try {
    const dir = await getPmtilesDir();
    const name = opfsFilenameFromUrl(url);
    const handle = await dir.getFileHandle(name);
    const file = await handle.getFile();
    return { exists: true, size: file.size };
  } catch (err) {
    // NotFoundError → Datei existiert nicht. Alles andere (OPFS nicht da,
    // Permission, etc.) behandeln wir auch als "nicht vorhanden" — der
    // Aufrufer triggert dann einen Download-Versuch, und der wird mit
    // einem sprechenderen Fehler scheitern.
    if ((err as { name?: string })?.name === 'NotFoundError') {
      return { exists: false, size: 0 };
    }
    console.warn(`[opfs] Status-Check für ${url} fehlgeschlagen`, err);
    return { exists: false, size: 0 };
  }
}

export interface DownloadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Lädt die URL komplett herunter und schreibt sie in OPFS unter dem
 * URL-abgeleiteten Dateinamen. Streamt direkt aus ReadableStream in den
 * FileSystemWritableFileStream — kein Zwischen-Buffering im RAM.
 *
 * Wave 14bd: kein `?_sw=bypass` mehr nötig, weil wir gar nicht mehr durch
 * CacheStorage gehen. Der Request läuft trotzdem durch den Service Worker
 * (`api-misc-v1` SWR-Regel in vite.config.ts) — das ist OK: wir lesen die
 * Bytes durch und werfen sie direkt in OPFS. Workbox's parallel-Cache ist
 * für uns irrelevant, weil wir ihn nie lesen.
 *
 * Atomic-Contract: bei Erfolg ist die Datei vollständig da. Bei Fehler/Abort
 * wird der teilweise geschriebene Eintrag entfernt — keine Zombie-Halbdateien.
 */
export async function downloadToOpfs(
  url: string,
  onProgress?: (p: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const dir = await getPmtilesDir();
  const name = opfsFilenameFromUrl(url);

  // Falls vor dem Download schon eine (evtl. teilweise / alte) Datei da ist:
  // wegräumen. Wir überschreiben sie zwar implizit beim Schreiben, aber ein
  // cleaner Start ist robuster bei Abbruch.
  try {
    await dir.removeEntry(name);
  } catch {
    /* nicht da — egal */
  }

  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();

  // Response holen. `cache: 'reload'` umgeht den HTTP-Cache und holt frisch
  // vom Server. Der Service-Worker darf sich gerne selbst was davon abschneiden
  // (api-misc-v1 SWR), das ist nicht unser Problem.
  let response: Response;
  try {
    response = await fetch(url, { signal, cache: 'reload' });
  } catch (err) {
    await writable.abort().catch(() => undefined);
    try {
      await dir.removeEntry(name);
    } catch {
      /* ignore */
    }
    throw err;
  }

  if (!response.ok || !response.body) {
    await writable.abort().catch(() => undefined);
    try {
      await dir.removeEntry(name);
    } catch {
      /* ignore */
    }
    throw new Error(`HTTP ${response.status} while loading ${url}`);
  }

  const total = Number(response.headers.get('Content-Length') ?? '0');
  const reader = response.body.getReader();
  let loaded = 0;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      // Chunk direkt in OPFS streamen — keine chunks[]-Buffer im JS-Heap.
      // Auf iPhone bei 120 MB Datei macht das den Unterschied zwischen
      // "App läuft rund" und "Safari killt den Tab wegen OOM".
      await writable.write(value);
      loaded += value.length;
      if (onProgress) {
        onProgress({
          loaded,
          total,
          percent: total ? Math.round((loaded / total) * 100) : 0,
        });
      }
    }
    await writable.close();
    // Wave 14ce-fix: Cache-Invalidierung an die OpfsPmtilesSource(s) feuern.
    // MUSS hier passieren, damit MapLibre's nächster Tile-Request die neue
    // Datei sieht statt der gecachten File-Referenz aus dem Vor-Download-State.
    dispatchOpfsUpdated(url);
  } catch (err) {
    // Aufräumen: Writable abbrechen (verwirft gepufferte Writes) + Dateieintrag
    // entfernen (damit opfsStatus() korrekt "nicht da" meldet und nicht eine
    // halbe Datei als valid betrachtet wird).
    try {
      await writable.abort();
    } catch {
      /* ignore */
    }
    try {
      await dir.removeEntry(name);
    } catch {
      /* ignore */
    }
    throw err;
  }
}

/**
 * Entfernt eine PMTiles-Datei aus OPFS. No-op wenn sie nicht da ist.
 */
export async function removeFromOpfs(url: string): Promise<void> {
  const dir = await getPmtilesDir();
  const name = opfsFilenameFromUrl(url);
  try {
    await dir.removeEntry(name);
  } catch (err) {
    if ((err as { name?: string })?.name === 'NotFoundError') {
      // Auch im NotFound-Fall invalidieren — die OpfsPmtilesSource könnte
      // noch eine alte File-Referenz halten, von der wir wollen dass sie
      // weg ist.
      dispatchOpfsUpdated(url);
      return;
    }
    throw err;
  }
  dispatchOpfsUpdated(url);
}

/**
 * Kompletter Wipe des pmtiles/-Verzeichnisses im OPFS. Für "Delete all
 * offline data"-Button in der OfflinePage.
 */
export async function clearAllOpfs(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) return;
  const root = await navigator.storage.getDirectory();
  try {
    // recursive: true entfernt das Verzeichnis inkl. aller Dateien. Falls der
    // Browser das nicht kennt (sehr alte Safari), gehen wir auf manuell durch.
    await root.removeEntry(PMTILES_DIR, { recursive: true });
  } catch (err) {
    if ((err as { name?: string })?.name === 'NotFoundError') {
      dispatchOpfsUpdated(null);
      return;
    }
    // Fallback: einzeln löschen
    try {
      const dir = await root.getDirectoryHandle(PMTILES_DIR);
      // @ts-expect-error — values() ist async iterable, noch nicht in lib.dom.d.ts typed
      for await (const entry of dir.values()) {
        try {
          await dir.removeEntry(entry.name);
        } catch {
          /* ignore single-file errors */
        }
      }
      await root.removeEntry(PMTILES_DIR).catch(() => undefined);
    } catch {
      /* falls selbst das scheitert, haben wir nichts mehr zu tun */
    }
  }
  // Wave 14ce-fix: alle Sources invalidieren (url=null = "alle").
  dispatchOpfsUpdated(null);
}

/**
 * Gibt den `File` für eine URL zurück — wenn im OPFS vorhanden. Sonst null.
 * Wird vom OpfsPmtilesSource in lib/map/pmtiles.ts benutzt um getBytes zu
 * servieren. File ist eine schlanke Blob-Referenz (keine in-memory copy).
 */
export async function openPmtilesFile(url: string): Promise<File | null> {
  try {
    const dir = await getPmtilesDir();
    const name = opfsFilenameFromUrl(url);
    const handle = await dir.getFileHandle(name);
    return await handle.getFile();
  } catch (err) {
    if ((err as { name?: string })?.name === 'NotFoundError') return null;
    console.warn(`[opfs] openPmtilesFile fehlgeschlagen für ${url}`, err);
    return null;
  }
}
